import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Icon } from '@iconify/react';
import { checkpointVariants } from './effects';
import useIsMobile from '../../hooks/isMobile';

const Checkpoint = ({ item, index, totalItems, isOpen, onClick }) => {
  const isLastItem = index === totalItems - 1;
  const isMobileView = useIsMobile();

  const cardContent = (
    <>
      <h3 className="font-bold text-md mb-1 text-neutral-900 dark:text-neutral-100">{item.title}</h3>
      <p className="text-sm font-semibold text-purple-500 mb-2">{item.subtitle}</p>
      <p className="text-xs text-neutral-600 dark:text-neutral-400">{item.details}</p>
    </>
  );

  return (
    <motion.div
      className="absolute"
      style={{ top: item.position.top, left: item.position.left }}
      variants={checkpointVariants}
    >
      <motion.div
        className="relative flex justify-center"
        onClick={onClick}
        whileHover={!isMobileView ? "visibleCard" : undefined}
        initial="hiddenCard"
        animate={!isMobileView && isOpen ? "visibleCard" : "hiddenCard"}
      >
        <div className="w-8 h-8 flex items-center justify-center transition-transform duration-300 transform hover:scale-125 cursor-pointer">
          <Icon
            icon={isLastItem ? "mdi:flag" : "mdi:map-marker"}
            className="text-red-500 w-full h-full drop-shadow-lg"
          />
        </div>
        <p className="absolute top-full mt-2 w-20 text-center text-xs font-medium text-neutral-700 dark:text-neutral-300">
          {item.place}
        </p>

        {/* DESKTOP: card stays inline, positioned relative to marker */}
        {!isMobileView && (
          <motion.div
            variants={{
              hiddenCard: { opacity: 0, y: 20, scale: 0.95, pointerEvents: 'none' },
              visibleCard: { opacity: 1, y: 0, scale: 1, zIndex: 10, pointerEvents: 'auto' },
            }}
            transition={{ duration: 0.3 }}
            className={`absolute bottom-full mb-4 w-64 bg-white dark:bg-neutral-800 rounded-lg shadow-xl p-4
              ${item.cardAlignment === 'left' ? 'right-full mr-4' : 'transform -translate-x-1/2 left-1/2'}`}
          >
            {cardContent}
          </motion.div>
        )}
      </motion.div>

      {/* MOBILE: portal straight to document.body, escapes all transforms */}
      {isMobileView && createPortal(
        <AnimatePresence>
          {isOpen && (
            <>
              <motion.div
                className="fixed inset-0 bg-black/40 z-[9998]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClick}
              />
              <motion.div
                className="fixed top-1/2 left-1/2 w-[85vw] max-w-xs bg-white dark:bg-neutral-800 rounded-lg shadow-xl p-4 z-[9999]"
                style={{ x: '-50%', y: '-50%' }}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
              >
                {cardContent}
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </motion.div>
  );
};

export default Checkpoint;